import React, { useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react';
import { useMinisiteSessionAnalytics } from '../analytics/webAnalytics';
import MonthlyLeadersScreen from './minisite/MonthlyLeadersScreen';
import RemoteNameKeyboard from './minisite/RemoteNameKeyboard';
import { fetchMinisiteLeaderboard, submitMinisiteLeaderboard } from '../api/minisiteLeaderboard';
import { qualifiesForLeaderboard } from '../utils/minisiteLeaderboardQualify';
import {
  createInitialTableState,
  startNewHand,
  runCpuActions,
  fold,
  check,
  call,
  raiseBy,
  collectHandResult,
  heroToCall,
  heroCanCheck,
  potTotal,
  streetLabelHe,
  HERO_SEAT,
  TABLE_ID,
  SMALL_BLIND,
  BIG_BLIND,
  ACTION_TIMER_SEC,
} from '../utils/poker/engine';
import { cardToString } from '../utils/poker/cards';
import { initRandomOpponents, ARCHETYPE_LABEL_HE } from '../utils/poker/aiProfiles';
import { amountToChipValues } from '../utils/blackjack/chipVisual';
import GsRadioChipPng from './GsRadioChipPng';
import './PokerGame.css';
import './minisite/MinisiteShared.css';

const SCORE_CAP = 500000;

const BACK_KEYS = new Set(['Escape', 'BrowserBack', 'GoBack']);

function PokerChipDisc({ value, className = '', style = {} }) {
  return <GsRadioChipPng value={value} className={`poker-chip-disc ${className}`.trim()} style={style} />;
}

function PokerGame() {
  useMinisiteSessionAnalytics();
  const tableRef = useRef(createInitialTableState());
  const betweenHandledRef = useRef(false);
  const seatElsRef = useRef({});
  const potRef = useRef(null);
  const blindAnimDoneSeqRef = useRef(null);
  const [blindFlights, setBlindFlights] = useState(null);
  const [uiTick, setUiTick] = useState(0);
  const bump = useCallback(() => setUiTick((t) => t + 1), []);

  const [screen, setScreen] = useState('leaders');
  const prevScreenRef = useRef('leaders');

  const [totalWon, setTotalWon] = useState(0);
  const [actionFocus, setActionFocus] = useState(0);
  const [raiseOpen, setRaiseOpen] = useState(false);
  const [raiseExtra, setRaiseExtra] = useState(BIG_BLIND);
  const [raiseFocus, setRaiseFocus] = useState(0);
  const [timerLeft, setTimerLeft] = useState(ACTION_TIMER_SEC);

  const [phase, setPhase] = useState('running');
  const [deadStep, setDeadStep] = useState('idle');
  const [joinSel, setJoinSel] = useState('no');
  const [nameInput, setNameInput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const scoreRef = useRef(0);
  const nextHandTimerRef = useRef(null);
  useEffect(() => {
    scoreRef.current = totalWon;
  }, [totalWon]);

  const goToLeaders = useCallback(() => {
    if (nextHandTimerRef.current) {
      clearTimeout(nextHandTimerRef.current);
      nextHandTimerRef.current = null;
    }
    betweenHandledRef.current = false;
    blindAnimDoneSeqRef.current = null;
    setBlindFlights(null);
    seatElsRef.current = {};
    tableRef.current = createInitialTableState();
    setPhase('running');
    setDeadStep('idle');
    setJoinSel('no');
    setNameInput('');
    setTotalWon(0);
    scoreRef.current = 0;
    setRaiseOpen(false);
    setScreen('leaders');
  }, []);

  const finalizeBetweenHands = useCallback(() => {
    const st = tableRef.current;
    if (st.phase !== 'between') {
      betweenHandledRef.current = false;
      return;
    }
    if (betweenHandledRef.current) return;
    betweenHandledRef.current = true;
    const gain = collectHandResult(st);
    if (gain > 0) {
      setTotalWon((w) => {
        const nw = Math.min(SCORE_CAP, w + gain);
        scoreRef.current = nw;
        return nw;
      });
    }
    if (!st.players[HERO_SEAT].stack) {
      setPhase('dead');
      return;
    }
    if (nextHandTimerRef.current) clearTimeout(nextHandTimerRef.current);
    nextHandTimerRef.current = setTimeout(() => {
      nextHandTimerRef.current = null;
      betweenHandledRef.current = false;
      startNewHand(st);
      runCpuActions(st);
      bump();
    }, 1000);
  }, [bump]);

  const afterHeroMutation = useCallback(() => {
    runCpuActions(tableRef.current);
    bump();
    finalizeBetweenHands();
  }, [bump, finalizeBetweenHands]);

  const startSession = useCallback(() => {
    blindAnimDoneSeqRef.current = null;
    setBlindFlights(null);
    const s = tableRef.current;
    initRandomOpponents(s);
    startNewHand(s);
    const r = runCpuActions(s);
    bump();
    if (s.phase === 'between') finalizeBetweenHands();
    if (r === 'between' && s.players[HERO_SEAT].stack <= 0) setPhase('dead');
  }, [bump, finalizeBetweenHands]);

  useEffect(() => {
    const was = prevScreenRef.current;
    prevScreenRef.current = screen;
    if (screen !== 'game') return undefined;
    if (was !== 'leaders') return undefined;
    betweenHandledRef.current = false;
    tableRef.current = createInitialTableState();
    setTotalWon(0);
    scoreRef.current = 0;
    setPhase('running');
    setDeadStep('idle');
    setRaiseOpen(false);
    startSession();
    return undefined;
  }, [screen, startSession]);

  useEffect(() => {
    if (phase !== 'dead') return undefined;
    let cancelled = false;
    setDeadStep('loading');
    (async () => {
      try {
        const d = await fetchMinisiteLeaderboard('poker');
        if (cancelled) return;
        const entries = d.entries || [];
        const sc = Math.min(Math.floor(scoreRef.current), SCORE_CAP);
        const show = qualifiesForLeaderboard('poker', entries, sc);
        setDeadStep(show ? 'joinAsk' : 'restartOnly');
      } catch {
        if (!cancelled) setDeadStep('restartOnly');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phase]);

  useEffect(() => {
    if (phase === 'running') setDeadStep('idle');
  }, [phase]);

  useLayoutEffect(() => {
    if (screen !== 'game' || phase !== 'running') return undefined;
    const st = tableRef.current;
    const p = st.pendingBlindAnim;
    if (!p) return undefined;
    if (blindAnimDoneSeqRef.current === p.handSeq) return undefined;

    const potEl = potRef.current;
    const seats = seatElsRef.current;
    const sbEl = seats[p.sbSeat];
    const bbEl = seats[p.bbSeat];
    if (!potEl || !sbEl || !bbEl) {
      st.pendingBlindAnim = null;
      blindAnimDoneSeqRef.current = p.handSeq;
      return undefined;
    }

    blindAnimDoneSeqRef.current = p.handSeq;
    st.pendingBlindAnim = null;

    const pot = potEl.getBoundingClientRect();
    const px = pot.left + pot.width / 2;
    const py = pot.top + pot.height / 2;
    const batches = [];
    const pushFlight = (seat, amount, delayBase) => {
      if (amount <= 0) return 0;
      const el = seats[seat];
      if (!el) return 0;
      const r = el.getBoundingClientRect();
      const fx = r.left + r.width / 2;
      const fy = r.top + r.height * 0.58;
      const chips = amountToChipValues(amount, 8);
      chips.forEach((v, i) => {
        batches.push({
          v,
          fx,
          fy,
          tx: px,
          ty: py,
          delay: delayBase + i * 42,
        });
      });
      return chips.length;
    };
    const nSb = pushFlight(p.sbSeat, p.sbAmount, 0);
    pushFlight(p.bbSeat, p.bbAmount, nSb * 42 + 72);
    setBlindFlights({ key: p.handSeq, batches, duration: 700 });
    return undefined;
  }, [uiTick, screen, phase]);

  useEffect(() => {
    if (!blindFlights?.batches?.length) return undefined;
    const maxD = Math.max(...blindFlights.batches.map((b) => b.delay), 0);
    const ms = blindFlights.duration + maxD + 80;
    const t = window.setTimeout(() => setBlindFlights(null), ms);
    return () => window.clearTimeout(t);
  }, [blindFlights]);

  const s = tableRef.current;
  void uiTick;
  const hero = s.players[HERO_SEAT];
  const heroTurn = s.phase === 'betting' && s.activeSeat === HERO_SEAT && !hero.folded;
  const tc = heroToCall(s);
  const canCheck = heroCanCheck(s);
  const pot = potTotal(s);

  const actions = React.useMemo(() => {
    const list = [];
    if (!heroTurn) return list;
    list.push({ id: 'fold', label: 'קיפול' });
    if (canCheck) list.push({ id: 'check', label: 'ודאי' });
    else list.push({ id: 'call', label: tc >= hero.stack ? `אול אין ${hero.stack}` : `קול ${Math.min(tc, hero.stack)}` });
    if (hero.stack > tc) list.push({ id: 'raise', label: 'העלאה' });
    return list;
  }, [heroTurn, canCheck, tc, hero.stack]);

  useEffect(() => {
    if (!heroTurn || raiseOpen) {
      setTimerLeft(ACTION_TIMER_SEC);
      return undefined;
    }
    let left = ACTION_TIMER_SEC;
    setTimerLeft(left);
    const id = setInterval(() => {
      left -= 1;
      setTimerLeft(left);
      if (left <= 0) {
        clearInterval(id);
        const st = tableRef.current;
        if (st.phase === 'betting' && st.activeSeat === HERO_SEAT) {
          if (heroCanCheck(st)) check(st, HERO_SEAT);
          else fold(st, HERO_SEAT);
          afterHeroMutation();
        }
      }
    }, 1000);
    return () => clearInterval(id);
  }, [heroTurn, raiseOpen, afterHeroMutation]);

  useEffect(() => {
    if (actionFocus >= actions.length) setActionFocus(Math.max(0, actions.length - 1));
  }, [actionFocus, actions.length]);

  const openRaise = useCallback(() => {
    const st = tableRef.current;
    const actv = st.players.filter((p) => !p.folded);
    const mb = actv.length ? Math.max(...actv.map((p) => p.betThisRound)) : 0;
    const p = st.players[HERO_SEAT];
    const toC = mb - p.betThisRound;
    const minExtra = Math.min(p.stack - toC, st.lastRaiseIncrement);
    setRaiseExtra(Math.max(BIG_BLIND, minExtra > 0 ? minExtra : BIG_BLIND));
    setRaiseFocus(0);
    setRaiseOpen(true);
  }, []);

  const applyRaise = useCallback(() => {
    setRaiseOpen(false);
    raiseBy(tableRef.current, HERO_SEAT, raiseExtra);
    afterHeroMutation();
  }, [raiseExtra, afterHeroMutation]);

  let raisePresets = { min: 0, halfPot: 0, pot: 0, allIn: 0 };
  if (raiseOpen) {
    const st = tableRef.current;
    const p = st.players[HERO_SEAT];
    const actv = st.players.filter((pl) => !pl.folded);
    const mb = actv.length ? Math.max(...actv.map((pl) => pl.betThisRound)) : 0;
    const toC = mb - p.betThisRound;
    const potSize = potTotal(st);
    const minR = st.lastRaiseIncrement;
    const maxExtra = Math.max(0, p.stack - toC);
    raisePresets = {
      min: Math.min(maxExtra, minR),
      halfPot: Math.min(maxExtra, Math.max(minR, Math.floor(potSize / 2))),
      pot: Math.min(maxExtra, Math.max(minR, potSize)),
      allIn: maxExtra,
    };
  }

  const raiseNavCount = 7;

  useEffect(() => {
    const onKey = (e) => {
      if (deadStep === 'name') return;
      if (phase === 'dead' && deadStep === 'loading') {
        e.preventDefault();
        return;
      }

      if (phase === 'dead' && deadStep === 'joinAsk') {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          setJoinSel((v) => (v === 'no' ? 'yes' : 'no'));
          e.preventDefault();
          return;
        }
        if (e.key === 'Enter' || e.keyCode === 13) {
          e.preventDefault();
          if (joinSel === 'yes') {
            setNameInput('');
            setDeadStep('name');
            return;
          }
          goToLeaders();
        }
        return;
      }

      if (phase === 'dead' && deadStep === 'thanks') {
        if (e.key === 'Enter' || e.keyCode === 13) {
          e.preventDefault();
          goToLeaders();
        }
        return;
      }

      if (screen !== 'game' || phase !== 'running') return;

      if (raiseOpen) {
        if (BACK_KEYS.has(e.key)) {
          e.preventDefault();
          setRaiseOpen(false);
          return;
        }
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          const dir = e.key === 'ArrowRight' ? -1 : 1;
          setRaiseFocus((f) => (f + dir + raiseNavCount) % raiseNavCount);
          return;
        }
        if (e.key === 'Enter' || e.keyCode === 13) {
          e.preventDefault();
          const st = tableRef.current;
          const p = st.players[HERO_SEAT];
          const actv = st.players.filter((pl) => !pl.folded);
          const mb = actv.length ? Math.max(...actv.map((pl) => pl.betThisRound)) : 0;
          const toC = mb - p.betThisRound;
          const maxExtra = Math.max(0, p.stack - toC);
          const potSize = potTotal(st);
          const minR = st.lastRaiseIncrement;
          switch (raiseFocus) {
            case 0:
              setRaiseExtra((x) => Math.max(minR, Math.min(maxExtra, x - BIG_BLIND)));
              break;
            case 1:
              setRaiseExtra((x) => Math.min(maxExtra, Math.max(minR, x + BIG_BLIND)));
              break;
            case 2:
              setRaiseExtra(Math.min(maxExtra, Math.max(minR, minR)));
              break;
            case 3:
              setRaiseExtra(Math.min(maxExtra, Math.max(minR, Math.floor(potSize / 2))));
              break;
            case 4:
              setRaiseExtra(Math.min(maxExtra, Math.max(minR, potSize)));
              break;
            case 5:
              setRaiseExtra(maxExtra);
              break;
            case 6:
              applyRaise();
              break;
            default:
              break;
          }
          return;
        }
        return;
      }

      if (!heroTurn) return;

      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const dir = e.key === 'ArrowRight' ? -1 : 1;
        if (actions.length === 0) return;
        setActionFocus((f) => (f + dir + actions.length) % actions.length);
        return;
      }

      if (e.key === 'Enter' || e.keyCode === 13) {
        e.preventDefault();
        const act = actions[actionFocus];
        if (!act) return;
        if (act.id === 'fold') {
          fold(tableRef.current, HERO_SEAT);
          afterHeroMutation();
        } else if (act.id === 'check') {
          check(tableRef.current, HERO_SEAT);
          afterHeroMutation();
        } else if (act.id === 'call') {
          call(tableRef.current, HERO_SEAT);
          afterHeroMutation();
        } else if (act.id === 'raise') {
          openRaise();
        }
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    screen,
    phase,
    deadStep,
    joinSel,
    goToLeaders,
    raiseOpen,
    raiseFocus,
    heroTurn,
    actions,
    actionFocus,
    afterHeroMutation,
    openRaise,
    applyRaise,
  ]);

  const handleNameDone = useCallback(async () => {
    const trimmed = nameInput.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      await submitMinisiteLeaderboard(
        'poker',
        trimmed,
        Math.floor(Math.min(scoreRef.current, SCORE_CAP))
      );
      setDeadStep('thanks');
    } catch {
      setDeadStep('thanks');
    } finally {
      setSubmitting(false);
    }
  }, [nameInput, submitting]);

  const handleNameCancel = useCallback(() => {
    setDeadStep('joinAsk');
    setNameInput('');
  }, []);

  const onLeadersContinue = useCallback(() => {
    setScreen('game');
  }, []);

  const restartFromDead = useCallback(() => {
    betweenHandledRef.current = false;
    tableRef.current = createInitialTableState();
    setTotalWon(0);
    scoreRef.current = 0;
    setPhase('running');
    setDeadStep('idle');
    startSession();
  }, [startSession]);

  if (screen === 'leaders') {
    return (
      <MonthlyLeadersScreen
        game="poker"
        scoreLabel="צ׳יפים שנזכו"
        onContinue={onLeadersContinue}
      />
    );
  }

  const aliveCount = s.players.filter((p) => p.stack > 0).length;

  const opponentName = (seat) => {
    const r = s.opponentRoster && s.opponentRoster[seat];
    return r?.displayName || 'שחקן';
  };
  const opponentArch = (seat) => {
    const r = s.opponentRoster && s.opponentRoster[seat];
    return r ? (ARCHETYPE_LABEL_HE[r.archetype] || '') : '';
  };

  const diffLabel = { easy: 'קל', medium: 'בינוני', hard: 'קשה' };

  const blindPreflop = s.handBlindSeats && s.street === 'preflop' && s.phase === 'betting';
  const seatBlindMark = (seat) => {
    if (!blindPreflop || !s.handBlindSeats) return null;
    if (s.handBlindSeats.sb === seat) {
      return <span className="poker-blind-mark">SB</span>;
    }
    if (s.handBlindSeats.bb === seat) {
      return <span className="poker-blind-mark">BB</span>;
    }
    return null;
  };

  const seatRoundBetChips = (p) => {
    if (s.phase === 'between' || p.betThisRound <= 0) return null;
    return (
      <div className="poker-seat-roundbet">
        {amountToChipValues(p.betThisRound, 4).map((v, i) => (
          <PokerChipDisc key={`rb-${i}-${v}`} value={v} className="poker-chip-disc--in-pot" />
        ))}
        <span className="poker-seat-roundbet-num">{p.betThisRound}</span>
      </div>
    );
  };

  const seatBankChips = (p) => (
    <div className="poker-seat-chips-row" aria-hidden>
      {amountToChipValues(Math.max(0, p.stack), 7).map((v, i) => (
        <PokerChipDisc key={`bk-${i}-${v}`} value={v} />
      ))}
    </div>
  );

  return (
    <div className="poker-page" dir="rtl">
      <div className="poker-felt" aria-hidden />

      <header className="poker-top">
        <span className="poker-top-item">בליינדים: {SMALL_BLIND}/{BIG_BLIND}</span>
        <span className="poker-top-item">שולחן #{TABLE_ID}</span>
        <span className="poker-top-item">{aliveCount} שחקנים</span>
        <span className="poker-top-item">רחוב: {streetLabelHe(s.street)}</span>
        <span className="poker-top-item">רמת שולחן: {diffLabel[s.aiDifficulty] || s.aiDifficulty}</span>
        {heroTurn && !raiseOpen ? (
          <span className="poker-top-item poker-top-timer">טיימר: {timerLeft}s</span>
        ) : null}
      </header>

      <div className="poker-table-wrap">
        <div className="poker-seats poker-seats--top">
          {[2, 3, 4].map((seat) => {
            const p = s.players[seat];
            const isDealer = s.dealerSeat === seat;
            return (
              <div
                key={seat}
                ref={(el) => {
                  seatElsRef.current[seat] = el;
                }}
                className={`poker-seat ${p.folded ? 'poker-seat--folded' : ''}`}
              >
                {isDealer ? <span className="poker-dealer">D</span> : null}
                {seatBlindMark(seat)}
                <span className="poker-seat-name">{opponentName(seat)}</span>
                {opponentArch(seat) ? (
                  <span className="poker-seat-arch">{opponentArch(seat)}</span>
                ) : null}
                {seatRoundBetChips(p)}
                {seatBankChips(p)}
                <span className="poker-seat-stack">{p.stack}</span>
              </div>
            );
          })}
        </div>

        <div className="poker-board">
          <div className="poker-community">
            {[0, 1, 2, 3, 4].map((i) => (
              <span key={i} className="poker-card">
                {s.community[i] ? cardToString(s.community[i]) : ''}
              </span>
            ))}
          </div>
          <div className="poker-pot" ref={potRef}>
            פוט: {pot}
          </div>
        </div>

        <div className="poker-seats poker-seats--bottom">
          <div
            ref={(el) => {
              seatElsRef.current[1] = el;
            }}
            className={`poker-seat ${s.players[1].folded ? 'poker-seat--folded' : ''}`}
          >
            {s.dealerSeat === 1 ? <span className="poker-dealer">D</span> : null}
            {seatBlindMark(1)}
            <span className="poker-seat-name">{opponentName(1)}</span>
            {opponentArch(1) ? <span className="poker-seat-arch">{opponentArch(1)}</span> : null}
            {seatRoundBetChips(s.players[1])}
            {seatBankChips(s.players[1])}
            <span className="poker-seat-stack">{s.players[1].stack}</span>
          </div>
          <div
            ref={(el) => {
              seatElsRef.current[0] = el;
            }}
            className={`poker-seat poker-seat--hero ${hero.folded ? 'poker-seat--folded' : ''}`}
          >
            {s.dealerSeat === 0 ? <span className="poker-dealer">D</span> : null}
            {seatBlindMark(0)}
            <span className="poker-seat-name">אני</span>
            <div className="poker-hole">
              {(hero.hole || []).map((c, i) => (
                <span key={i} className="poker-card poker-card--hole">{c ? cardToString(c) : '·'}</span>
              ))}
            </div>
            {seatRoundBetChips(hero)}
            {seatBankChips(hero)}
            <span className="poker-seat-stack">מאגר: {hero.stack}</span>
            {tc > 0 && !canCheck ? (
              <span className="poker-tocall">לקול: {Math.min(tc, hero.stack)}</span>
            ) : null}
          </div>
          <div
            ref={(el) => {
              seatElsRef.current[5] = el;
            }}
            className={`poker-seat ${s.players[5].folded ? 'poker-seat--folded' : ''}`}
          >
            {s.dealerSeat === 5 ? <span className="poker-dealer">D</span> : null}
            {seatBlindMark(5)}
            <span className="poker-seat-name">{opponentName(5)}</span>
            {opponentArch(5) ? <span className="poker-seat-arch">{opponentArch(5)}</span> : null}
            {seatRoundBetChips(s.players[5])}
            {seatBankChips(s.players[5])}
            <span className="poker-seat-stack">{s.players[5].stack}</span>
          </div>
        </div>
      </div>

      <nav className="poker-actions" aria-label="פעולות">
        {actions.map((a, i) => (
          <span
            key={a.id}
            className={`poker-act ${i === actionFocus ? 'poker-act--on' : ''}`}
          >
            {a.label}
          </span>
        ))}
      </nav>

      {blindFlights && blindFlights.batches.length > 0
        ? blindFlights.batches.map((b, i) => (
            <div
              key={`bf-${blindFlights.key}-${i}`}
              className="poker-chip-fly-wrap"
              style={{ left: b.fx, top: b.fy }}
            >
              <PokerChipDisc
                value={b.v}
                className="poker-chip-fly"
                style={{
                  '--tx': `${b.tx - b.fx}px`,
                  '--ty': `${b.ty - b.fy}px`,
                  animationDuration: `${blindFlights.duration}ms`,
                  animationDelay: `${b.delay}ms`,
                }}
              />
            </div>
          ))
        : null}

      <p className="poker-hint">
        {heroTurn && !raiseOpen ? '← → לבחירה · Enter לאישור' : ''}
        {heroTurn && raiseOpen ? '← → בתפריט העלאה · Enter לפעולה · Back לביטול' : ''}
        {!heroTurn && s.phase === 'betting' && !hero.folded ? 'המתן לתורך…' : ''}
        {!heroTurn && s.phase === 'betting' && hero.folded ? 'צופה ביד…' : ''}
        {s.phase === 'between' ? 'מחלקים יד חדשה…' : ''}
        {' · '}סה״כ צ׳יפים שנזכו: {Math.min(totalWon, SCORE_CAP)}
      </p>

      {raiseOpen ? (
        <div className="poker-overlay">
          <div className="poker-raise-panel">
            <h2 className="poker-raise-title">העלאה</h2>
            <div className="poker-raise-stepper">
              <span className={raiseFocus === 0 ? 'poker-rf-on' : ''}>−</span>
              <span className="poker-raise-amt">{raiseExtra}</span>
              <span className={raiseFocus === 1 ? 'poker-rf-on' : ''}>+</span>
            </div>
            <div className="poker-raise-presets">
              {[
                { i: 2, label: 'מינ׳', val: raisePresets.min },
                { i: 3, label: 'חצי פוט', val: raisePresets.halfPot },
                { i: 4, label: 'פוט', val: raisePresets.pot },
                { i: 5, label: 'אול אין', val: raisePresets.allIn },
              ].map((row) => (
                <span key={row.i} className={raiseFocus === row.i ? 'poker-rf-on' : ''}>
                  {row.label}
                </span>
              ))}
            </div>
            <span className={raiseFocus === 6 ? 'poker-rf-on poker-raise-confirm' : 'poker-raise-confirm'}>
              אשר העלאה
            </span>
          </div>
        </div>
      ) : null}

      {phase === 'dead' ? (
        <div className="poker-overlay">
          <div className="poker-dead-panel">
            <div className="poker-dead-title">נגמר המאגר</div>
            <div className="poker-dead-score">
              צ׳יפים שנזכו: {Math.min(Math.floor(totalWon), SCORE_CAP)}
            </div>
            {deadStep === 'loading' ? (
              <div className="minisite-overlay-busy">בודקים זכאות לטבלה…</div>
            ) : null}
            {deadStep === 'restartOnly' ? (
              <div className="poker-dead-hint">לחץ Enter לשחק שוב</div>
            ) : null}
            {deadStep === 'joinAsk' ? (
              <div className="minisite-join">
                <p className="minisite-join-q">להצטרף למצטייני החודש?</p>
                <div className="minisite-join-options">
                  <span className={`minisite-join-opt ${joinSel === 'no' ? 'minisite-join-opt--on' : ''}`}>
                    לא
                  </span>
                  <span className={`minisite-join-opt ${joinSel === 'yes' ? 'minisite-join-opt--on' : ''}`}>
                    כן
                  </span>
                </div>
                <div className="poker-dead-hint">← → לבחירה · Enter לאישור</div>
              </div>
            ) : null}
            {deadStep === 'name' ? (
              <RemoteNameKeyboard
                value={nameInput}
                onChange={setNameInput}
                onDone={handleNameDone}
                onCancel={handleNameCancel}
              />
            ) : null}
            {deadStep === 'thanks' ? (
              <div className="poker-dead-hint">נרשמת בהצלחה! לחץ Enter לחזרה למצטייני החודש</div>
            ) : null}
            {submitting ? <div className="minisite-overlay-busy">שומר…</div> : null}
          </div>
        </div>
      ) : null}

      {phase === 'dead' && deadStep === 'restartOnly' ? (
        <DeadEnterListener onEnter={restartFromDead} />
      ) : null}
    </div>
  );
}

function DeadEnterListener({ onEnter }) {
  useEffect(() => {
    const fn = (e) => {
      if (e.key === 'Enter' || e.keyCode === 13) {
        e.preventDefault();
        onEnter();
      }
    };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [onEnter]);
  return null;
}

export default PokerGame;
